import { motion } from "framer-motion";
import { ImagePlus, MapPin } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

// Live installation sites — set photo paths here when the real photos arrive
const SITES = [
  { name: "Prateek Edifice", location: "Noida", photo: "/photos/prateek-edifice.jpg" },
  { name: "Gaur Saundaryam", location: "Greater Noida", photo: "/photos/gaur-saundaryam.jpg" },
];

const QuoteCue = () => (
  <span className="pointer-events-none absolute inset-0 bg-black/0 group-hover/img:bg-black/40 transition-colors duration-300 flex items-center justify-center">
    <span className="opacity-0 group-hover/img:opacity-100 transition-opacity duration-300 text-white text-[10px] font-bold tracking-[0.25em] uppercase border-2 border-white px-4 py-2">
      Get a Quote
    </span>
  </span>
);

export default function Inventory({ scrollTo }) {
  const onQuote = () => scrollTo?.("#contact");

  return (
    <section id="inventory" data-testid="inventory-section" className="py-24 md:py-32 border-t border-black/10">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-4 mb-6"
        >
          <span className="w-3 h-3 bg-brand-red" />
          <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-black/60">Live Installations</span>
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-display uppercase leading-[0.95] tracking-tight text-5xl md:text-7xl text-black max-w-4xl mb-14 md:mb-20"
        >
          Real Sites. Real Screens.
        </motion.h2>

        <div className="grid md:grid-cols-2 gap-5">
          {SITES.map((site, i) => (
            <motion.figure
              key={site.name}
              data-testid={`inventory-site-${i + 1}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, ease: EASE, delay: i * 0.12 }}
            >
              <button
                data-testid={`inventory-image-link-${i + 1}`}
                onClick={onQuote}
                aria-label="Get a quote"
                className="group/img relative block w-full clip-corner aspect-[16/10] border border-black/10 overflow-hidden cursor-pointer"
              >
                {site.photo ? (
                  <img
                    src={site.photo}
                    alt={site.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover/img:scale-105"
                  />
                ) : (
                  <span className="absolute inset-0 bg-[#F7F7F7] border-2 border-dashed border-brand-red/40 group-hover/img:border-brand-red flex flex-col items-center justify-center gap-3 transition-colors duration-300">
                    <ImagePlus className="w-8 h-8 text-brand-red/60" strokeWidth={1.5} />
                    <span className="text-[10px] font-bold tracking-[0.25em] uppercase text-brand-red/70">
                      Site photo — upload pending
                    </span>
                  </span>
                )}
                <QuoteCue />
              </button>
              <figcaption className="mt-4 flex items-start justify-between gap-3">
                <span className="font-display uppercase tracking-wide text-3xl text-black">{site.name}</span>
                <span className="flex items-center gap-1.5 text-xs font-semibold text-black/50 mt-2 shrink-0">
                  <MapPin className="w-3.5 h-3.5 text-brand-red" />
                  {site.location}
                </span>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
